import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { useEstablecimiento } from '@/features/establecimientos/hooks/useEstablecimientos';
import { useProfesional } from '@/features/profesionales/hooks/useProfesionales';
import { useNotify } from '@/hooks/useNotify';
import { isApiError } from '@/lib/apiError';
import { formatDateAR } from '@/utils/formatters';

import { useDeleteProtocolo } from '../hooks/useProtocolos';

function FichaRow({
  label,
  loading,
  opcional = false,
  children,
}: {
  label: string;
  loading: boolean;
  opcional?: boolean;
  children: string | null;
}): JSX.Element {
  const falta = !loading && children === null;
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.25, alignItems: 'baseline' }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={160} />
      ) : falta ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No disponible
          </Typography>
          {!opcional && (
            <Tooltip title="Información faltante">
              <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>
      ) : (
        <Typography variant="body2">{children}</Typography>
      )}
    </Box>
  );
}

interface EliminarProtocoloDialogProps {
  open: boolean;
  onClose: () => void;
  protocolo: {
    uid_protocolo: string;
    numero_protocolo: number;
    fecha_protocolo: string;
    uid_establecimiento: string;
    uid_profesional: string;
    cantidad_analisis?: number;
    cantidad_envios?: number;
    informe_parcial_generado?: boolean;
    informe_final_emitido: boolean;
  };
  onEliminado?: () => void;
}

export function EliminarProtocoloDialog({
  open,
  onClose,
  protocolo,
  onEliminado,
}: EliminarProtocoloDialogProps): JSX.Element {
  const notify = useNotify();
  const deleteMutation = useDeleteProtocolo();
  const [textoConfirmacion, setTextoConfirmacion] = useState('');

  const establecimientoQuery = useEstablecimiento(open ? protocolo.uid_establecimiento : undefined);
  const profesionalQuery = useProfesional(open ? protocolo.uid_profesional : undefined);

  const establecimiento = establecimientoQuery.data;
  const profesional = profesionalQuery.data;
  const loadingMeta = establecimientoQuery.isLoading || profesionalQuery.isLoading;

  const firmaGanaderos = establecimiento?.firma_ganaderos ?? null;

  const textoEsperado = `protocolo ${protocolo.numero_protocolo}`;
  const esValido = textoConfirmacion.trim() === textoEsperado;

  useEffect(() => {
    if (!open) {
      setTextoConfirmacion('');
      deleteMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const items: string[] = [];
  const countersKnown =
    protocolo.cantidad_analisis != null ||
    protocolo.cantidad_envios != null ||
    protocolo.informe_parcial_generado != null;

  if (protocolo.cantidad_analisis != null && protocolo.cantidad_analisis > 0)
    items.push(`${protocolo.cantidad_analisis} análisis cargados`);
  if (protocolo.cantidad_envios != null && protocolo.cantidad_envios > 0)
    items.push(`${protocolo.cantidad_envios} envíos registrados`);
  if (protocolo.informe_parcial_generado)
    items.push('Informe parcial con su PDF');
  if (protocolo.informe_final_emitido)
    items.push('Informe final con su PDF');

  // Fotos: solo podemos decir que existen si hay análisis
  if (
    (protocolo.cantidad_analisis == null || protocolo.cantidad_analisis > 0) &&
    countersKnown
  ) {
    items.push('Fotos asociadas a los análisis (si las hubiera)');
  }

  // Cuando no tenemos ningún contador, mostramos la lista genérica completa
  const itemsAMostrar =
    !countersKnown
      ? [
          'Análisis cargados y sus fotos',
          'Envíos registrados',
          'Informes con sus PDFs',
        ]
      : items;

  async function handleEliminar(): Promise<void> {
    try {
      await deleteMutation.mutateAsync(protocolo.uid_protocolo);
      notify.success(`Protocolo N° ${protocolo.numero_protocolo} eliminado.`);
      onClose();
      onEliminado?.();
    } catch (err) {
      if (isApiError(err) && err.errorCode === 'protocolo_bloqueado') {
        notify.error('Este protocolo tiene informe final emitido y no se puede eliminar.');
        onClose();
      } else if (isApiError(err) && err.errorCode === 'cascade_delete_parcial') {
        notify.warning(
          'Algunos elementos no se pudieron eliminar. Intentá nuevamente. Si el problema persiste, contactá a soporte.',
        );
        setTextoConfirmacion('');
      } else {
        notify.error('Error al eliminar el protocolo. Intentá nuevamente.');
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={deleteMutation.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Eliminar protocolo</DialogTitle>

      <DialogContent>
        <Typography variant="body2" gutterBottom>
          Estás por eliminar el{' '}
          <strong>Protocolo N° {protocolo.numero_protocolo}</strong> y todo su contenido
          asociado:
        </Typography>

        <Box
          sx={{
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          <FichaRow label="Establecimiento" loading={loadingMeta}>
            {establecimiento?.nombre ?? null}
          </FichaRow>
          <FichaRow label="Firma ganaderos" loading={loadingMeta} opcional>
            {firmaGanaderos}
          </FichaRow>
          <FichaRow label="Veterinario" loading={loadingMeta}>
            {profesional?.nombre_apellido ?? null}
          </FichaRow>
          <FichaRow label="Fecha del protocolo" loading={false}>
            {formatDateAR(protocolo.fecha_protocolo)}
          </FichaRow>
          {protocolo.cantidad_analisis != null && (
            <FichaRow label="Análisis cargados" loading={false}>
              {String(protocolo.cantidad_analisis)}
            </FichaRow>
          )}
        </Box>

        {itemsAMostrar.length > 0 ? (
          <List dense disablePadding sx={{ mb: 1 }}>
            {itemsAMostrar.map((item) => (
              <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary={`• ${item}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Este protocolo no tiene contenido asociado.
          </Typography>
        )}

        <Typography variant="body2" color="error" fontWeight={500} mt={1}>
          Esta acción no se puede deshacer.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Para confirmar, escribí exactamente:
        </Typography>

        <Box
          sx={{
            bgcolor: 'grey.100',
            p: 1.5,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '1.1em',
            textAlign: 'center',
            my: 1.5,
          }}
        >
          {textoEsperado}
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder={textoEsperado}
          value={textoConfirmacion}
          onChange={(e) => setTextoConfirmacion(e.target.value)}
          disabled={deleteMutation.isPending}
          autoFocus
          autoComplete="off"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={deleteMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={() => void handleEliminar()}
          disabled={!esValido || deleteMutation.isPending}
          color="error"
          variant="contained"
          startIcon={
            deleteMutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteIcon />
            )
          }
        >
          Eliminar todo
        </Button>
      </DialogActions>
    </Dialog>
  );
}
